import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Navbar } from '@packages/ui/layout';
import type { NavMenuGroup } from '@packages/ui/layout';
import CheckPermissions from '@/utils/permission/checkPermission';

interface NavMenuRendererProps {
  groups: NavMenuGroup[];
}

/**
 * Generic desktop nav renderer.
 * Iterates over NavMenuGroup[], renders NavItem or NavDropdown,
 * and wraps in CheckPermissions when a group declares a permission.
 */
const NavMenuRenderer: React.FC<NavMenuRendererProps> = ({ groups }) => {
  const location = useLocation();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const isGroupActive = (group: NavMenuGroup) => {
    if (group.path && isActive(group.path)) return true;
    return (
      group.children?.some((child) =>
        location.pathname.startsWith(child.path.split('#')[0])
      ) ?? false
    );
  };

  return (
    <Navbar.Nav semanticId="desktopNav">
      {groups.filter((g) => g.visible).map((group) => {
        const hasChildren = group.children && group.children.length > 0;

        const content = hasChildren ? (
          <Navbar.NavDropdown
            key={group.id}
            label={group.label}
            active={isGroupActive(group)}
            semanticId={`${group.id}Dropdown`}
          >
            {group.children!.filter((c) => c.visible).map((child) => (
              <Navbar.NavDropdownItem key={child.id} semanticId={`${child.id}DropdownItem`}>
                <NavLink
                  to={child.path}
                  className={({ isActive: a }) => (a ? 'active' : '')}
                >
                  {child.label}
                </NavLink>
              </Navbar.NavDropdownItem>
            ))}
          </Navbar.NavDropdown>
        ) : (
          <Navbar.NavItem
            key={group.id}
            active={isActive(group.path ?? '')}
            semanticId={`${group.id}NavItem`}
          >
            <NavLink
              to={group.path!}
              className={({ isActive: a }) => (a ? 'active' : '')}
            >
              {group.label}
            </NavLink>
          </Navbar.NavItem>
        );

        if (group.permission) {
          return (
            <CheckPermissions
              key={group.id}
              type={group.permission.type}
              urlName={group.permission.urlName}
            >
              {content}
            </CheckPermissions>
          );
        }

        return content;
      })}
    </Navbar.Nav>
  );
};

export { NavMenuRenderer };
